// Editable staff list + add-staff search, shared by StaffModal and CreatePlanWizard's staff step
// (BlockForm.tsx precedent for extracted shared form pieces). Controlled: the caller owns `staff`.
// Each person gets three stacked permission checkboxes. Plan Manager implies the other two.
import { Button, Checkbox, Select, Tooltip, Typography } from "antd";
import { DeleteOutlined, UserAddOutlined } from "@ant-design/icons";
import { getInitials } from "@utils/rtp_utils";
import { coachName, useCoachList, useCurrentUserUuid } from "./rtpHooks";
import type { RtpStaffFlags } from "./types";

const { Text } = Typography;

export const emptyStaffFlags = (): RtpStaffFlags => ({
	CanManagePlan: false,
	CanCreateDayPlan: false,
	CanCreateExitCriteria: false,
});

const PERMISSIONS: { key: keyof RtpStaffFlags; label: string }[] = [
	{ key: "CanManagePlan", label: "Plan Manager (full access)" },
	{ key: "CanCreateDayPlan", label: "Can create training sessions" },
	{ key: "CanCreateExitCriteria", label: "Can create exit criteria for phases" },
];

interface StaffFlagsEditorProps {
	staff: Record<string, RtpStaffFlags>;
	// The staff as saved on the plan. When given, a row whose Plan Manager value differs shows a
	// note about what happens on save. The create wizard has no saved staff, so it passes nothing.
	savedStaff?: Record<string, RtpStaffFlags>;
	onChange: (next: Record<string, RtpStaffFlags>) => void;
	getName: (uuid: string) => string;
	removeDataAttr: string;
}

export const StaffFlagsEditor = ({ staff, savedStaff, onChange, getName, removeDataAttr }: StaffFlagsEditorProps) => {
	const coachList = useCoachList();
	const currentUserUuid = useCurrentUserUuid();

	// Plan Manager turns on all three flags, and the other two stay on (and locked) while it is
	// on. Clearing Plan Manager keeps the other two as they were, so the change is easy to undo.
	const setPermission = (uuid: string, key: keyof RtpStaffFlags, checked: boolean) => {
		const next: RtpStaffFlags =
			key === "CanManagePlan" && checked
				? { CanManagePlan: true, CanCreateDayPlan: true, CanCreateExitCriteria: true }
				: { ...staff[uuid], [key]: checked };
		onChange({ ...staff, [uuid]: next });
	};

	const removeStaff = (uuid: string) => {
		const next = { ...staff };
		delete next[uuid];
		onChange(next);
	};

	const rows = Object.entries(staff).map(([uuid, flags]) => ({ uuid, ...flags }));
	const availableCoaches = coachList.filter((c) => !(c.uuid in staff));
	const hasManager = rows.some((row) => row.CanManagePlan);

	return (
		<div className="rtp-staff-editor">
			<Select
				showSearch
				className="rtp-staff-add"
				prefix={<UserAddOutlined />}
				placeholder="Add staff by name"
				value={null}
				optionFilterProp="label"
				notFoundContent="No matching staff"
				options={availableCoaches.map((c) => ({ value: c.uuid, label: coachName(c) }))}
				onChange={(uuid: string) => onChange({ ...staff, [uuid]: emptyStaffFlags() })}
			/>

			<div className="rtp-staff-section">
				<span>On this plan</span>
				<span className="rtp-staff-count">{rows.length}</span>
			</div>

			<div className="rtp-staff-list">
				{rows.length === 0 ? (
					<div className="rtp-staff-empty">No staff yet. Add someone with the search above.</div>
				) : null}
				{rows.map((row) => {
					const name = getName(row.uuid);
					const isYou = row.uuid === currentUserUuid;
					const wasManager = savedStaff ? Boolean(savedStaff[row.uuid]?.CanManagePlan) : row.CanManagePlan;
					const managerNote =
						wasManager === row.CanManagePlan
							? null
							: row.CanManagePlan
								? { tone: "gain", text: "Becomes a Plan Manager when you save." }
								: isYou
									? { tone: "self", text: "You will lose access to edit this plan and its staff when you save." }
									: { tone: "lose", text: "Loses Plan Manager access when you save." };
					return (
						<div className="rtp-staff-row" key={row.uuid}>
							<div className="rtp-staff-avatar">{getInitials(name)}</div>
							<div className="rtp-staff-main">
								<div className="rtp-staff-name">
									<span className="rtp-staff-name-text">{name}</span>
									{isYou ? <span className="rtp-staff-you">You</span> : null}
								</div>
								<div className="rtp-staff-perms">
									{PERMISSIONS.map(({ key, label }) => (
										<Checkbox
											key={key}
											checked={row[key]}
											disabled={key !== "CanManagePlan" && row.CanManagePlan}
											onChange={(e) => setPermission(row.uuid, key, e.target.checked)}
										>
											{label}
										</Checkbox>
									))}
								</div>
								{managerNote ? (
									<div className={`rtp-staff-note rtp-staff-note--${managerNote.tone}`}>{managerNote.text}</div>
								) : null}
							</div>
							<Tooltip title="Remove from plan">
								<Button
									type="text"
									size="small"
									className="rtp-staff-remove"
									icon={<DeleteOutlined />}
									aria-label={`Remove ${name}`}
									data-attr={removeDataAttr}
									onClick={() => removeStaff(row.uuid)}
								/>
							</Tooltip>
						</div>
					);
				})}
			</div>

			{rows.length > 0 && !hasManager ? (
				<Text className="rtp-staff-warning">Make at least one person a manager.</Text>
			) : null}
			<Text type="secondary" className="rtp-staff-hint">
				Sign-off rights are set on each exit criteria, not here.
			</Text>
		</div>
	);
};
